import { useState, useRef, useEffect, useCallback } from 'react';

const PENCIL_ICON = (
    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
    </svg>
);

const ARROW_ICON = (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
    </svg>
);

export function QuestionUI({ questions, onSubmit, isSubmitting }) {
    const [currentStep, setCurrentStep] = useState(0);
    const [answers, setAnswers] = useState({});
    const [otherText, setOtherText] = useState('');
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const [otherFocused, setOtherFocused] = useState(false);
    const otherInputRef = useRef(null);

    const currentQuestion = questions[currentStep];
    const isLastStep = currentStep === questions.length - 1;
    const isFirstStep = currentStep === 0;
    const totalSteps = questions.length;
    const regularOptions = currentQuestion.options.filter(opt => !opt.input);
    const previousAnswer = answers[currentQuestion.question];
    const isCustomPreviousAnswer = previousAnswer && !regularOptions.some(opt => opt.label === previousAnswer);

    useEffect(() => {
        setOtherText(isCustomPreviousAnswer ? previousAnswer : '');
        setFocusedIndex(-1);
        setOtherFocused(false);
    }, [currentStep]);

    const submitAll = useCallback((finalAnswers) => {
        const completeAnswers = {};
        questions.forEach(q => {
            completeAnswers[q.question] = finalAnswers[q.question] || '[No preference]';
        });
        onSubmit({ answers: completeAnswers });
    }, [questions, onSubmit]);

    const handleSelectOption = useCallback((label) => {
        if (isSubmitting) return;
        const newAnswers = { ...answers, [currentQuestion.question]: label };
        setAnswers(newAnswers);
        setOtherText('');
        setFocusedIndex(-1);

        if (isLastStep) {
            submitAll(newAnswers);
        } else {
            setCurrentStep(prev => prev + 1);
        }
    }, [isSubmitting, answers, currentQuestion, isLastStep, submitAll]);

    const handleOtherSubmit = useCallback(() => {
        if (!otherText.trim() || isSubmitting) return;
        handleSelectOption(otherText.trim());
    }, [otherText, isSubmitting, handleSelectOption]);

    const handleSkip = useCallback(() => {
        if (isSubmitting) return;
        const completeAnswers = {};
        questions.forEach(q => {
            completeAnswers[q.question] = answers[q.question] || '[No preference]';
        });
        onSubmit({ answers: completeAnswers, declined: true });
    }, [isSubmitting, questions, answers, onSubmit]);

    const handlePrevStep = useCallback(() => {
        if (!isFirstStep) {
            setCurrentStep(prev => prev - 1);
        }
    }, [isFirstStep]);

    const handleNextStep = useCallback(() => {
        if (!isLastStep && answers[currentQuestion.question]) {
            setCurrentStep(prev => prev + 1);
        }
    }, [isLastStep, answers, currentQuestion]);

    useEffect(() => {
        if (otherFocused) return;

        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                handleSkip();
                return;
            }
            if (e.key === 'ArrowUp') {
                e.preventDefault();
                setFocusedIndex(prev => prev <= 0 ? regularOptions.length - 1 : prev - 1);
                return;
            }
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setFocusedIndex(prev => prev >= regularOptions.length - 1 ? 0 : prev + 1);
                return;
            }
            if (e.key === 'Enter' && focusedIndex >= 0 && focusedIndex < regularOptions.length) {
                e.preventDefault();
                handleSelectOption(regularOptions[focusedIndex].label);
                return;
            }
            const num = parseInt(e.key);
            if (num >= 1 && num <= regularOptions.length) {
                e.preventDefault();
                handleSelectOption(regularOptions[num - 1].label);
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [otherFocused, focusedIndex, regularOptions, handleSelectOption, handleSkip]);

    if (currentQuestion.multi_select || currentQuestion.multiSelect) {
        return (
            <MultiSelectQuestion
                question={currentQuestion}
                answers={answers}
                setAnswers={setAnswers}
                isSubmitting={isSubmitting}
                onNext={(updatedAnswers) => {
                    if (isLastStep) {
                        submitAll(updatedAnswers || answers);
                    } else {
                        setCurrentStep(prev => prev + 1);
                    }
                }}
                onSkip={handleSkip}
                onPrev={handlePrevStep}
                totalSteps={totalSteps}
                currentStep={currentStep}
                isFirstStep={isFirstStep}
                isLastStep={isLastStep}
            />
        );
    }

    return (
        <div>
            <div className="flex items-start justify-between gap-3 mb-5">
                <h3 className="text-base font-medium text-gray-700 leading-snug flex-1">
                    {currentQuestion.question}
                </h3>
                <div className="flex items-center gap-2 flex-shrink-0">
                    {totalSteps > 1 && (
                        <div className="flex items-center gap-1 text-xs text-gray-400">
                            <button
                                onClick={handlePrevStep}
                                disabled={isFirstStep || isSubmitting}
                                className="p-0.5 hover:text-gray-600 disabled:opacity-30 disabled:cursor-default transition-colors"
                            >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                </svg>
                            </button>
                            <span>{currentStep + 1} of {totalSteps}</span>
                            <button
                                onClick={handleNextStep}
                                disabled={isLastStep || !answers[currentQuestion.question] || isSubmitting}
                                className="p-0.5 hover:text-gray-600 disabled:opacity-30 disabled:cursor-default transition-colors"
                            >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                            </button>
                        </div>
                    )}
                    <button
                        onClick={handleSkip}
                        disabled={isSubmitting}
                        className="p-1 text-gray-400 hover:text-gray-600 transition-colors rounded-md hover:bg-gray-100"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
            </div>

            <div className="-mx-3">
                <div onMouseLeave={() => setFocusedIndex(-1)}>
                    {regularOptions.map((opt, idx) => {
                        const isFocused = focusedIndex === idx && !otherFocused;
                        const isSelected = opt.label === previousAnswer;
                        const isLast = idx === regularOptions.length - 1;
                        return (
                            <div key={idx}>
                                <button
                                    onClick={() => handleSelectOption(opt.label)}
                                    onMouseEnter={() => { setFocusedIndex(idx); setOtherFocused(false); }}
                                    disabled={isSubmitting}
                                    className={`w-full flex items-center gap-3.5 px-3 py-3 text-left rounded-xl transition-colors group disabled:opacity-50 ${isFocused ? 'bg-blue-50' : 'hover:bg-blue-50'}`}
                                >
                                    <span className={`flex-shrink-0 w-7 h-7 rounded-full text-xs font-medium flex items-center justify-center transition-colors ${isSelected ? 'bg-blue-500 text-white' : isFocused ? 'bg-blue-100 text-blue-600' : 'bg-blue-50 text-blue-400 group-hover:bg-blue-100 group-hover:text-blue-600'}`}>
                                        {isSelected ? (
                                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                            </svg>
                                        ) : idx + 1}
                                    </span>
                                    <span className={`flex-1 text-sm ${isSelected ? 'text-gray-800 font-medium' : 'text-gray-600'}`}>{opt.label}</span>
                                    <svg className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-opacity ${isFocused ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                                    </svg>
                                </button>
                                {!isLast && (
                                    <div className={`mx-3 border-b transition-colors ${isFocused || (focusedIndex === idx + 1 && !otherFocused) ? 'border-transparent' : 'border-gray-100'}`} />
                                )}
                            </div>
                        );
                    })}
                    <div className={`mx-3 border-b transition-colors ${focusedIndex === regularOptions.length - 1 && !otherFocused || otherFocused ? 'border-transparent' : 'border-gray-100'}`} />
                </div>

                <div className={`flex items-center gap-3.5 px-3 py-3 rounded-xl transition-colors ${otherFocused || otherText ? 'bg-blue-50' : ''}`}>
                    <span className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-50 text-blue-400 flex items-center justify-center">
                        {PENCIL_ICON}
                    </span>
                    <input
                        ref={otherInputRef}
                        type="text"
                        value={otherText}
                        onChange={(e) => setOtherText(e.target.value)}
                        onFocus={() => { setOtherFocused(true); setFocusedIndex(-1); }}
                        onBlur={() => setOtherFocused(false)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') handleOtherSubmit();
                            if (e.key === 'Escape' && !otherText) {
                                otherInputRef.current?.blur();
                            }
                        }}
                        placeholder="Khác"
                        className="flex-1 text-sm text-gray-600 placeholder-gray-400 bg-transparent border-none outline-none"
                        disabled={isSubmitting}
                    />
                    {otherText.trim() ? (
                        <button
                            onClick={handleOtherSubmit}
                            disabled={isSubmitting}
                            className="flex-shrink-0 w-7 h-7 rounded-lg bg-gray-800 text-white flex items-center justify-center hover:bg-gray-700 transition-colors disabled:opacity-50"
                        >
                            {ARROW_ICON}
                        </button>
                    ) : (
                        <button
                            onClick={handleSkip}
                            disabled={isSubmitting}
                            className="flex-shrink-0 text-sm text-gray-400 hover:text-gray-500 px-3 py-1 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors disabled:opacity-50"
                        >
                            Bỏ qua
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

function MultiSelectQuestion({ question, answers, setAnswers, isSubmitting, onNext, onSkip, onPrev, totalSteps, currentStep, isFirstStep, isLastStep }) {
    const [otherText, setOtherText] = useState('');
    const [otherFocused, setOtherFocused] = useState(false);
    const [hoveredIndex, setHoveredIndex] = useState(-1);
    const otherInputRef = useRef(null);

    const selected = answers[question.question] ? answers[question.question].split(', ').filter(Boolean) : [];
    const regularOptions = question.options.filter(opt => !opt.input);

    const toggleOption = (label) => {
        if (isSubmitting) return;
        const newSelected = selected.includes(label)
            ? selected.filter(l => l !== label)
            : [...selected, label];
        setAnswers({ ...answers, [question.question]: newSelected.join(', ') });
    };

    const handleOtherAdd = () => {
        if (!otherText.trim() || isSubmitting) return;
        const newSelected = [...selected, otherText.trim()];
        setAnswers({ ...answers, [question.question]: newSelected.join(', ') });
        setOtherText('');
    };

    const handleSubmit = () => {
        let finalAnswers = answers;
        if (otherText.trim()) {
            const newSelected = [...selected, otherText.trim()];
            finalAnswers = { ...answers, [question.question]: newSelected.join(', ') };
            setAnswers(finalAnswers);
            setOtherText('');
        }
        onNext(finalAnswers);
    };

    return (
        <div>
            <div className="flex items-start justify-between gap-3 mb-5">
                <h3 className="text-base font-medium text-gray-700 leading-snug flex-1">
                    {question.question}
                </h3>
                <div className="flex items-center gap-2 flex-shrink-0">
                    {totalSteps > 1 && (
                        <div className="flex items-center gap-1 text-xs text-gray-400">
                            <button
                                onClick={onPrev}
                                disabled={isFirstStep || isSubmitting}
                                className="p-0.5 hover:text-gray-600 disabled:opacity-30 disabled:cursor-default transition-colors"
                            >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                </svg>
                            </button>
                            <span>{currentStep + 1} of {totalSteps}</span>
                            <button
                                disabled={true}
                                className="p-0.5 disabled:opacity-30 disabled:cursor-default"
                            >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                            </button>
                        </div>
                    )}
                    <button
                        onClick={onSkip}
                        disabled={isSubmitting}
                        className="p-1 text-gray-400 hover:text-gray-600 transition-colors rounded-md hover:bg-gray-100"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
            </div>

            <div className="-mx-3">
                <div onMouseLeave={() => setHoveredIndex(-1)}>
                    {regularOptions.map((opt, idx) => {
                        const isChecked = selected.includes(opt.label);
                        const isHovered = hoveredIndex === idx;
                        const isLast = idx === regularOptions.length - 1;
                        return (
                            <div key={idx}>
                                <button
                                    onClick={() => toggleOption(opt.label)}
                                    onMouseEnter={() => setHoveredIndex(idx)}
                                    disabled={isSubmitting}
                                    className={`w-full flex items-center gap-3.5 px-3 py-3 text-left rounded-xl transition-colors group disabled:opacity-50 ${isHovered ? 'bg-blue-50' : 'hover:bg-blue-50'}`}
                                >
                                    <span className={`flex-shrink-0 w-5 h-5 rounded border-[1.5px] flex items-center justify-center transition-colors ${isChecked ? 'bg-blue-500 border-blue-500' : 'border-gray-300 group-hover:border-gray-400'}`}>
                                        {isChecked && (
                                            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                            </svg>
                                        )}
                                    </span>
                                    <span className="flex-1 text-sm text-gray-600">{opt.label}</span>
                                </button>
                                {!isLast && (
                                    <div className={`mx-3 border-b transition-colors ${isHovered || hoveredIndex === idx + 1 ? 'border-transparent' : 'border-gray-100'}`} />
                                )}
                            </div>
                        );
                    })}
                    <div className={`mx-3 border-b transition-colors ${hoveredIndex === regularOptions.length - 1 || otherFocused ? 'border-transparent' : 'border-gray-100'}`} />
                </div>

                <div className={`flex items-center gap-3.5 px-3 py-3 rounded-xl transition-colors ${otherFocused || otherText ? 'bg-blue-50' : ''}`}>
                    <span className={`flex-shrink-0 w-5 h-5 rounded border-[1.5px] flex items-center justify-center transition-colors ${otherFocused || otherText ? 'border-gray-400' : 'border-gray-300'}`} />
                    <input
                        ref={otherInputRef}
                        type="text"
                        value={otherText}
                        onChange={(e) => setOtherText(e.target.value)}
                        onFocus={() => setOtherFocused(true)}
                        onBlur={() => setOtherFocused(false)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') handleOtherAdd();
                        }}
                        placeholder="Khác"
                        className="flex-1 text-sm text-gray-600 placeholder-gray-400 bg-transparent border-none outline-none"
                        disabled={isSubmitting}
                    />
                    {otherText.trim() && (
                        <button
                            onClick={handleOtherAdd}
                            disabled={isSubmitting}
                            className="flex-shrink-0 w-7 h-7 rounded-lg bg-gray-800 text-white flex items-center justify-center hover:bg-gray-700 transition-colors disabled:opacity-50"
                        >
                            {ARROW_ICON}
                        </button>
                    )}
                </div>
            </div>

            <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
                <span className={`text-sm transition-colors ${selected.length > 0 ? 'text-blue-500' : 'text-gray-400'}`}>
                    {selected.length > 0 ? `${selected.length} lựa chọn` : 'Chưa chọn'}
                </span>
                <div className="flex items-center gap-2">
                    <button
                        onClick={onSkip}
                        disabled={isSubmitting}
                        className="text-sm text-gray-400 hover:text-gray-500 px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                        Bỏ qua
                    </button>
                    {(selected.length > 0 || otherText.trim()) && (
                        <button
                            onClick={handleSubmit}
                            disabled={isSubmitting}
                            className="flex-shrink-0 w-8 h-8 rounded-lg bg-gray-800 text-white flex items-center justify-center hover:bg-gray-700 transition-colors disabled:opacity-50"
                        >
                            {isLastStep ? (
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.5 10.5L12 3m0 0l7.5 7.5M12 3v18" />
                                </svg>
                            ) : ARROW_ICON}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
